'use client';

import { FC, useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { toast } from '@/hotdesk-ui/components/toast-v4';
import { JSONWebToken, MinUser, SendCode, UserRegistration } from 'hotdesk_typescript';
import { ApiConfigration } from '@/hotdesk-ui/common-classes/api-configuration';
import { AuthService, SocialLoginAccountConflictError, SocialLoginCancelledError } from '@/hotdesk-ui/services/auth.service';
import { UserService } from '@/hotdesk-ui/services/user.service';
import { firebaseAnalytics } from '@/hotdesk-ui/common-classes/firebase.service';
import { LoginServerError } from '@/hotdesk-ui/types/login.type';
import { SignUpServerError, SignUpServerErrorsData } from '@/hotdesk-ui/types/singup.type';
import { dispatch as storeDispatch, RootState } from '@/hotdesk-ui/store/store';
import { setUserData } from '@/hotdesk-ui/store/slices/auth.slice';
import { useSelector } from 'react-redux';
import AuthShell from './AuthShell';
import EmailScreen from './EmailScreen';
import PasswordScreen from './PasswordScreen';
import SignUpScreen, { SignUpFormValues, SignUpServerErrors } from './SignUpScreen';
import ResetPasswordScreen from './ResetPasswordScreen';
import VerifyScreen from './VerifyScreen';
type Step = 'email' | 'password' | 'signup' | 'reset' | 'verify';
export interface AuthModalV4Props {
  show: boolean;
  handleClose: () => void;
  language: string;
  termLink: string;
  privacyPolicyLink: string;
  envUrl: string;
  googleLogin: boolean;
  appleLogin: boolean;
  isItida?: boolean;
  initialStep?: Step | null;
  initialEmail?: string | null;
}
const getAuthService = async (languageCode: string): Promise<AuthService> => {
  const apiConfig = await new ApiConfigration().getApiConfig(languageCode);
  return new AuthService(apiConfig);
};
const getUserService = async (languageCode: string): Promise<UserService> => {
  const apiConfig = await new ApiConfigration().getApiConfig(languageCode);
  return new UserService(apiConfig);
};
const AuthModalV4: FC<AuthModalV4Props> = ({
  show,
  handleClose,
  language,
  termLink,
  privacyPolicyLink,
  envUrl,
  googleLogin,
  appleLogin,
  isItida = false,
  initialStep = null,
  initialEmail = null
}) => {
  const t = useTranslations();
  const router = useRouter();
  const reduxUser = useSelector((state: RootState) => state.auth.user);

  // Passed into AuthService.login (and the social variants) so a login
  // on booking/workspaces/bookingDetails/subscriptions pages re-renders
  // the server components with the fresh auth cookie (subscription-aware
  // SSR props) instead of hard-reloading — Redux and client state
  // survive, no flash. See the audit note on AuthService.login.
  const refreshAfterAuth = () => router.refresh();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [matchedUser, setMatchedUser] = useState<MinUser | undefined>();
  const [signUpServerErrors, setSignUpServerErrors] = useState<SignUpServerErrors | undefined>();
  const [isSubmittingEmail, setIsSubmittingEmail] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [loginErrorKey, setLoginErrorKey] = useState<string | undefined>();
  const [verifyState, setVerifyState] = useState<{
    phoneNumber: string;
    userId?: number;
    userEmail?: string;
    userPassword?: string;
  } | null>(null);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [verifyInvalid, setVerifyInvalid] = useState(false);
  useEffect(() => {
    if (!show) return;
    if (initialStep) setStep(initialStep);
    if (initialEmail) setEmail(initialEmail);
  }, [show, initialStep, initialEmail]);
  const reset = () => {
    setStep('email');
    setEmail('');
    setMatchedUser(undefined);
    setSignUpServerErrors(undefined);
    setLoginErrorKey(undefined);
    setVerifyState(null);
    setVerifyInvalid(false);
    setIsSubmittingEmail(false);
    setIsLoggingIn(false);
    setIsSigningUp(false);
    setVerifyLoading(false);
  };
  const close = () => {
    reset();
    handleClose();
  };
  const onEmailSubmit = async (submittedEmail: string) => {
    setIsSubmittingEmail(true);
    try {
      await firebaseAnalytics('web_signup_or_login', {
        method: 'email'
      });
      const service = await getAuthService(language);
      const userExists = (await service.getUserbyEmail(submittedEmail)) as MinUser;
      setEmail(submittedEmail);
      if (userExists) {
        // Accounts created via "Continue with Google/Apple" have no
        // password, so routing them to the password screen would
        // always fail. Redirect straight into the matching social flow.
        const registrationSource = userExists.registrationSource?.toLowerCase();
        if (registrationSource === 'google') {
          await onLoginWithGoogle();
          return;
        }
        if (registrationSource === 'apple') {
          await onLoginWithApple();
          return;
        }
        setMatchedUser(userExists);
        setStep('password');
      } else {
        setStep('signup');
      }
    } finally {
      setIsSubmittingEmail(false);
    }
  };

  // The email belongs to an existing account created with a different
  // source than the social provider that was pressed. Password (Hotdesk)
  // accounts are routed to the password screen with an explanatory
  // message; cross-provider accounts (e.g. pressing Google on an
  // Apple-created account) stay on the email screen with a toast naming
  // the right provider — chaining a second OAuth popup would likely be
  // blocked by the browser.
  const handleSocialConflict = (conflict: SocialLoginAccountConflictError) => {
    const source = conflict.existingUser.registrationSource?.toLowerCase();
    if (source === 'google' || source === 'apple') {
      toast.error(t(source === 'google' ? 'auth.logIn.googleAccount' : 'auth.logIn.appleAccount'), {
        position: 'bottom-center'
      });
      setStep('email');
      return;
    }
    setEmail(conflict.existingUser.email);
    setMatchedUser(conflict.existingUser);
    setLoginErrorKey('auth.logIn.passwordAccount');
    setStep('password');
  };
  const onLoginWithGoogle = async () => {
    await firebaseAnalytics('web_signup_or_login', {
      method: 'google'
    });
    try {
      const service = await getAuthService(language);
      await service.loginWithGoogle(refreshAfterAuth);
      close();
    } catch (error) {
      if (error instanceof SocialLoginCancelledError) {
        // User dismissed the Google popup — keep the modal open, no toast.
        return;
      }
      if (error instanceof SocialLoginAccountConflictError) {
        handleSocialConflict(error);
        return;
      }
      const {
        body
      } = error as LoginServerError || {
        body: {
          nonFieldErrors: []
        }
      };
      const msg = body?.nonFieldErrors?.[0] || 'Unable to log in with provided credentials';
      toast.error(msg, {
        position: 'bottom-center'
      });
    }
  };
  const onLoginWithApple = async () => {
    await firebaseAnalytics('web_signup_or_login', {
      method: 'apple'
    });
    try {
      const service = await getAuthService(language);
      await service.loginWithApple(refreshAfterAuth);
      close();
    } catch (error) {
      if (error instanceof SocialLoginCancelledError) {
        // User dismissed the Apple popup — keep the modal open, no toast.
        return;
      }
      if (error instanceof SocialLoginAccountConflictError) {
        handleSocialConflict(error);
        return;
      }
      toast.error(String(error), {
        position: 'bottom-center'
      });
    }
  };
  const onPasswordSubmit = async (password: string) => {
    if (!matchedUser) return;
    setIsLoggingIn(true);
    setLoginErrorKey(undefined);
    try {
      const service = await getAuthService(language);
      await firebaseAnalytics('web_login_success');
      await service.login({
        username: matchedUser.email,
        password
      } as JSONWebToken, refreshAfterAuth);
      close();
    } catch (error) {
      const loginError = error as LoginServerError;
      const nonFieldErrors = loginError?.body?.nonFieldErrors;
      const errorMessage = nonFieldErrors?.[0] ?? '';
      const key = errorMessage.indexOf('credentials') !== -1 ? 'auth.logIn.invalidCredentials' : 'auth.logIn.otherError';
      setLoginErrorKey(key);
      await firebaseAnalytics('web_login_password_error', {
        error: errorMessage
      });
    } finally {
      setIsLoggingIn(false);
    }
  };
  const onForgotPassword = async () => {
    await firebaseAnalytics('web_reset_password_clicked');
    window.open(envUrl, '_blank');
  };
  const onResetSubmit = async (resetEmail: string) => {
    try {
      const userService = await getUserService(language);
      await userService.generateMagicLink(resetEmail);
      setEmail(resetEmail);
    } catch (error: any) {
      const message = error?.body?.detail || error?.message || 'Unable to send reset link';
      toast.error(message, {
        position: 'bottom-center'
      });
      throw error;
    }
  };
  const buildServerErrors = (body?: SignUpServerErrorsData & {
    detail?: string;
  }): SignUpServerErrors => {
    const errs: SignUpServerErrors = {};
    if (!body) return errs;
    if (body.email && body.email[0]) {
      errs.email = t('auth.signUp.uniqueEmail');
    }
    if (body.phoneNumber && body.phoneNumber[0]) {
      const first = body.phoneNumber[0];
      if (first === 'phone_not_mobile_error') {
        errs.phone = t('auth.signUp.invalidPhoneNumber');
      } else if (first === 'Enter a valid phone number.') {
        errs.phone = t('auth.signUp.phone-error');
      } else {
        errs.phone = t('auth.signUp.uniquePhone');
      }
    }
    if (body.itidaId && body.itidaId[0]) {
      errs.itidaId = body.itidaId[0];
    }
    if (body.detail && typeof body.detail === 'string') {
      if (isItida && !errs.itidaId) {
        errs.itidaId = body.detail;
      } else if (!errs.nonField) {
        errs.nonField = body.detail;
      }
    }
    if (body.nonFieldErrors && body.nonFieldErrors[0]) {
      errs.nonField = body.nonFieldErrors[0];
    }
    return errs;
  };
  const getSignUpErrorToastMessage = (body?: SignUpServerErrorsData & {
    detail?: string;
  }, serverErrors?: SignUpServerErrors): string | undefined => {
    if (body?.detail && typeof body.detail === 'string') {
      return body.detail;
    }
    if (serverErrors?.nonField) return serverErrors.nonField;
    if (serverErrors?.email) return serverErrors.email;
    if (serverErrors?.phone) return serverErrors.phone;
    if (serverErrors?.itidaId) return serverErrors.itidaId;
    if (body?.nonFieldErrors?.[0]) return body.nonFieldErrors[0];
    return undefined;
  };
  const showSignUpErrorToast = (body?: SignUpServerErrorsData & {
    detail?: string;
  }, serverErrors?: SignUpServerErrors) => {
    const message = getSignUpErrorToastMessage(body, serverErrors);
    if (message) {
      toast.error(message, {
        position: 'bottom-center'
      });
    }
  };
  const onSignUp = async (data: SignUpFormValues) => {
    setIsSigningUp(true);
    setSignUpServerErrors(undefined);
    const userSignUpData: UserRegistration = {
      firstName: data.firstName,
      lastName: data.lastName ?? '',
      phoneNumber: data.phone,
      email: data.email,
      password: data.password,
      username: data.email,
      meta: {}
    } as UserRegistration;
    if (isItida && data.itidaId && data.itidaId.trim()) {
      userSignUpData.itidaId = data.itidaId.trim();
    }
    try {
      const service = await getAuthService(language);
      const registerResponse = await service.register(userSignUpData);
      const responseAny = registerResponse as any;
      if (responseAny.errors || responseAny.itidaId) {
        const errorBody = {
          email: responseAny.errors?.email || [''],
          username: responseAny.errors?.username || [''],
          phoneNumber: responseAny.errors?.phoneNumber || [''],
          itidaId: responseAny.errors?.itidaId || responseAny.itidaId || [''],
          nonFieldErrors: responseAny.errors?.nonFieldErrors || [''],
          detail: responseAny.detail
        };
        const serverErrors = buildServerErrors(errorBody);
        setSignUpServerErrors(serverErrors);
        showSignUpErrorToast(errorBody, serverErrors);
        return;
      }
      if (isItida && data.itidaId && data.itidaId.trim()) {
        sessionStorage.setItem('itida_signup', 'true');
      }
      await service.sendVarificationEmail({
        email: data.email
      });
      if (registerResponse.phoneNumber) {
        try {
          await firebaseAnalytics('web_otp_request_sent');
          await service.sendPhoneCode({
            username: registerResponse.username,
            phoneNumber: registerResponse.phoneNumber.replace(/\s+/g, '')
          });
          setVerifyState({
            phoneNumber: registerResponse.phoneNumber,
            userId: registerResponse.id,
            userEmail: registerResponse.username,
            userPassword: userSignUpData.password
          });
          setStep('verify');
        } catch {
          await service.login({
            username: registerResponse.username,
            password: userSignUpData.password
          }, refreshAfterAuth);
          close();
        }
      } else {
        close();
      }
    } catch (error) {
      const apiError = error as SignUpServerError & {
        response?: {
          data?: SignUpServerErrorsData & {
            detail?: string;
          };
        };
        data?: SignUpServerErrorsData & {
          detail?: string;
        };
      };
      const body = apiError?.body || apiError?.response?.data || apiError?.data;
      await firebaseAnalytics('web_signup_error', {
        error: JSON.stringify(body)
      });
      const serverErrors = buildServerErrors(body);
      setSignUpServerErrors(serverErrors);
      showSignUpErrorToast(body, serverErrors);
    } finally {
      setIsSigningUp(false);
    }
  };
  const onVerify = async (otp: string) => {
    if (!verifyState) return;
    setVerifyLoading(true);
    setVerifyInvalid(false);
    try {
      const service = await getAuthService(language);
      const userService = await getUserService(language);
      if (!verifyState.userId) return;
      const response = await service.varifyPhoneNumber(verifyState.userId, {
        pinCode: otp
      });
      if (response) {
        toast.success('Phone number verified successfully.', {
          position: 'bottom-center'
        });
      }
      if (reduxUser) {
        const updated = await userService.updateUser(reduxUser.id ?? 0, {
          firstName: reduxUser.firstName,
          lastName: reduxUser.lastName,
          email: reduxUser.email,
          username: reduxUser.username,
          isPhoneVerified: true
        });
        if (updated) storeDispatch(setUserData(updated));
      }
      await firebaseAnalytics('web_otp_accepted');
      if (verifyState.userEmail && verifyState.userPassword) {
        await service.login({
          username: verifyState.userEmail,
          password: verifyState.userPassword
        }, refreshAfterAuth);
      }
      close();
    } catch (error) {
      await firebaseAnalytics('web_otp_error', {
        error: JSON.stringify(error)
      });
      setVerifyInvalid(true);
    } finally {
      setVerifyLoading(false);
    }
  };
  const onResendVerify = async () => {
    if (!verifyState) return;
    try {
      await firebaseAnalytics('web_otp_request_resent');
      const service = await getAuthService(language);
      const sendCode: SendCode = {
        phoneNumber: verifyState.phoneNumber.replace(/\s+/g, ''),
        username: verifyState.userEmail ?? reduxUser?.username
      } as SendCode;
      if (sendCode.username && sendCode.phoneNumber) {
        await service.sendPhoneCode(sendCode);
      }
      setVerifyInvalid(false);
    } catch (error: any) {
      const message = error?.message || error?.body?.detail || 'Unable to resend';
      toast.error(message, {
        position: 'bottom-center'
      });
    }
  };
  const onSkipVerify = async () => {
    if (!verifyState) return;
    await firebaseAnalytics('web_verify_later');
    try {
      const service = await getAuthService(language);
      if (verifyState.userEmail && verifyState.userPassword) {
        await service.login({
          username: verifyState.userEmail,
          password: verifyState.userPassword
        }, refreshAfterAuth);
      }
    } finally {
      close();
    }
  };
  const onBack = useMemo(() => {
    if (step === 'email') return undefined;
    return () => {
      if (step === 'reset') setStep('password');else if (step === 'verify') setStep('signup');else setStep('email');
      setLoginErrorKey(undefined);
      setSignUpServerErrors(undefined);
      setVerifyInvalid(false);
      setIsSubmittingEmail(false);
      setIsLoggingIn(false);
      setIsSigningUp(false);
      setVerifyLoading(false);
    };
  }, [step]);
  const renderStep = () => {
    switch (step) {
      case 'email':
        return <EmailScreen onSubmit={onEmailSubmit} onGoogleLogin={onLoginWithGoogle} onAppleLogin={onLoginWithApple} onSwitchToSignUp={() => {
          setEmail('');
          setStep('signup');
        }} googleLoginEnabled={googleLogin} appleLoginEnabled={appleLogin} termLink={termLink} privacyPolicyLink={privacyPolicyLink} isSubmitting={isSubmittingEmail} />;
      case 'password':
        return <PasswordScreen firstName={matchedUser?.firstName} onSubmit={onPasswordSubmit} onForgotPassword={onForgotPassword} isLoading={isLoggingIn} serverError={loginErrorKey ? t(loginErrorKey) : undefined} termLink={termLink} privacyPolicyLink={privacyPolicyLink} />;
      case 'signup':
        return <SignUpScreen initialEmail={email} isItida={isItida} isLoading={isSigningUp} serverErrors={signUpServerErrors} termLink={termLink} privacyPolicyLink={privacyPolicyLink} onSubmit={onSignUp} />;
      case 'reset':
        return <ResetPasswordScreen initialEmail={email} onSubmit={onResetSubmit} termLink={termLink} privacyPolicyLink={privacyPolicyLink} />;
      case 'verify':
        if (!verifyState) return null;
        return <VerifyScreen phoneNumber={verifyState.phoneNumber} isLoading={verifyLoading} isInvalid={verifyInvalid} onVerify={onVerify} onResend={onResendVerify} onSkip={onSkipVerify} termLink={termLink} privacyPolicyLink={privacyPolicyLink} />;
    }
  };
  return <AuthShell show={show} onClose={close} onBack={onBack} data-sentry-element="AuthShell" data-sentry-component="AuthModalV4" data-sentry-source-file="AuthModalV4.tsx">
            {renderStep()}
        </AuthShell>;
};
export default AuthModalV4;